import { createLabManifest } from "@/helpers/lab_utils/labManifest";
import {
  createLabPassage,
  completionPassageExtras
} from "@/helpers/lab_utils/labPassage";

const passages = [
  createLabPassage({
    id: "start",
    progressLabel: "Welcome",
    title: "Ion Selective Electrode Lab",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Welcome to the Ion Selective Electrode Lab."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The purpose of this experiment is to introduce you to: i) the use of an ion selective electrode other than a pH electrode to obtain analytical data, ii) apply an alternative method to carry out the standard addition for determination of concentrations in unknown samples and iii) investigate electrode selectivity and interference by a second ion."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this experiment, the concentration of Ca\u00B2\u207A in an unknown solution will be measured. The potential sensed by a sodium ion sensitive electrode for each solution will be recorded and analyzed."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "principles"
      }
    ]
  }),

  createLabPassage({
    id: "principles",
    progressLabel: "Principles",
    title: "Principles",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The ion selective electrode (ISE) provides a relatively easy and inexpensive way to measure certain ion concentrations. These electrodes are generally made from a very thin glass, polymer or a crystalline membrane specific to the analysis carried out. In general, the cell potential of the ISE is expressed in terms of the ion activity by:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: { latex: "E_{ind} = L - S \\log a_{Ca^{2+}}" }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "where the response factor S is about 59.2/n mV per decade at 25\u00B0C (therefore, for a Ca\u00B2\u207A ion it should be close to 30 mV/pCa). Eq. 1 can be rewritten in terms of an activity coefficient as:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex: "E_{ind} = L - S \\log \\gamma_{Ca^{2+}} [Ca^{2+}]"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In practice, the ionic strength of the solution is kept constant by adding a constant amount of a solution with a high ionic strength, called an ionic strength adjusting buffer (ISAB), to all standards and samples. The activity coefficient is then approximately constant and can be factored out and included in the constant term L. The potential should now be linear with the log of the ion concentration:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex:
                  "E_{ind} = L' - S \\log_{Ca^{2+}} [Ca^{2+}] = L' - S \\log [Ca^{2+}]"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Since the electrode you will be using today includes an Ag/AgCl reference electrode filled with KCl solution, the ISAB used will be KCl such that the solution under analysis is 1.0 \u00B1 0.1M, which will minimize the formation of a junction potential in the system."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "part-1-calibration"
      }
    ]
  }),

  createLabPassage({
    id: "part-1-calibration",
    progressLabel: "Part 1: Calibration",
    title: "Part 1: Building a Calibration Curve",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Open the Lab Area to begin. You will build a calibration curve by adding known calcium standard solutions to a base ISAB solution and recording the voltage readings."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Steps:"
                }
              ]
            },
            {
              type: "orderedList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Start with the base ISAB solution in the beaker."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Add small volumes (500 \u00B5L or 1000 \u00B5L) of calcium standard solutions (100 ppm, 1,000 ppm, or 10,000 ppm) using the Lab Area controls."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "After each addition, take a reading and record the voltage (mV) displayed on the meter."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "The tool tracks the running cumulative concentration in the beaker. You will use voltage vs. log[Ca\u00B2\u207A] to plot your calibration curve."
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Expect approximately 30 mV increase in voltage for every 10\u00D7 increase in calcium concentration."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "part-2-magnesium"
      }
    ]
  }),

  createLabPassage({
    id: "part-2-magnesium",
    progressLabel: "Part 2: Mg Interference",
    title: "Part 2: Testing Magnesium Interference",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Reset the beaker to a fresh ISAB base solution. In this part you will investigate how magnesium ions interfere with calcium measurements."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Steps:"
                }
              ]
            },
            {
              type: "orderedList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Add 1000 \u00B5L of 10,000 ppm Mg solution and take a voltage reading."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Add 1000 \u00B5L of 10,000 ppm Ca solution and take a second voltage reading."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Record both voltage readings. You will use these two values to calculate the selectivity coefficient and determine how much magnesium interferes with the calcium measurement."
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "part-3-unknown"
      }
    ]
  }),

  createLabPassage({
    id: "part-3-unknown",
    progressLabel: "Part 3: Unknown",
    title: "Part 3: Finding the Unknown Concentration",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Reset the beaker to a fresh ISAB base solution. In this part you will use the standard addition method to determine the concentration of an unknown Ca\u00B2\u207A sample."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Steps:"
                }
              ]
            },
            {
              type: "orderedList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Add 1000 \u00B5L of the unknown sample and take a voltage reading."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Add 1000 \u00B5L of the 10,000 ppm Ca standard and take a second voltage reading."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Use the two voltage readings and the standard addition formula to calculate the unknown calcium concentration."
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "When you have collected all readings, save your attempt. You can export your data as a CSV from the Attempts view for analysis in Excel, Python, or other tools. You may attempt the activity a total of 5 times."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Save Attempt",
        to: "lab-complete"
      }
    ]
  }),

  createLabPassage({
    id: "lab-complete",
    progressLabel: "Complete",
    title: "Lab Complete",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You have completed the Ion Selective Electrode Lab."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this laboratory you have:"
                }
              ]
            },
            {
              type: "bulletList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Built a calibration curve of voltage vs. log[Ca\u00B2\u207A] concentration"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Investigated magnesium interference on the calcium-selective electrode"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Determined an unknown Ca\u00B2\u207A concentration using the standard addition method"
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Export your data from the Attempts view to complete your analysis and prepare your memo-style lab report."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [],
    ...completionPassageExtras("lab-complete")
  })
];

/** @type {import("@/types/labManifest.types.js").LabManifest} */
export const labManifest = createLabManifest({
  id: "ion-selective-electrode-lab",
  displayName: "Ion Selective Electrode Lab",
  labAreaUnlockPassageId: "part-1-calibration",
  completionPassageId: "lab-complete",
  passages
});

export default {
  draft: passages
};
export { passages };
